
import React, { ReactElement, useMemo } from "react";
import { withTranslation } from "react-i18next";
import {
  IChildrenProps,
  IClassNameProps,
  IIdProps,
  ILabelProps,
  ITranslationPros
} from "../controlsPropertiesInterface";
import "./baseFieldNew.scss";
import { ValueType } from "./fieldPropertiesInterfaceNew";

interface IPropsBaseField
  extends IIdProps,
    ILabelProps,
    ITranslationPros,
    IChildrenProps,
    IClassNameProps {
  prefix?: ReactElement;
  sufix?: ReactElement;
  onClearState?: () => void;
  attr?: string;
  value?: ValueType;
  disabled?: boolean;
  required?: boolean;
  isError?: boolean;
  hideSingleClearState?: boolean;
  disabledBorder?: boolean;
  isClassicInput?: boolean;
}

/** Función reutilizable para detectar "vacío" */
function isValueEmpty(v: ValueType): boolean {
  // null / undefined
  if (v == null) return true;

  // Date (si querés considerar Date como "no vacío", lo tratamos como lleno)
  if (v instanceof Date) return isNaN(v.getTime()); // fecha inválida => vacío

  // Arrays
  if (Array.isArray(v)) return v.length === 0;

  // Objetos
  if (typeof v === "object") {
    // Evita fallo si value no es un objeto plano
    try {
      return Object.keys(v).length === 0;
    } catch {
      return false;
    }
  }

  // Strings
  if (typeof v === "string") return v.trim().length === 0;

  // Numbers (0 lo consideramos "no vacío"; NaN sí)
  if (typeof v === "number") return Number.isNaN(v);

  // Boolean / otros tipos => en general no vacío
  return false;
}

function BaseField({
  id,
  prefix,
  onClearState,
  children,
  sufix,
  label,
  attr,
  value,
  required,
  className,
  isError,
  t,
  hideSingleClearState,
  isClassicInput,
  disabledBorder
}: IPropsBaseField) {
  const hideRequiredAsterisk = () => !required;

  const isEmptyValue = useMemo(() => isValueEmpty(value as ValueType), [value]);

  const handleClearState = () => {
    if (typeof onClearState === "function") {
      onClearState();
    }
  };

  const containerClasses = [
    (isClassicInput) ? "classic-input-container" : "input-container",
    "field-container-new", // marca unica para no colisionar con los estilos de baseField.scss
    (disabledBorder) ? "border-none" : "",
    !isEmptyValue ? "border-active active-label" : "",
    className || "",
    isError ? "border border-danger" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses}>
      <div className="input-container__prefix">{prefix}</div>

      {children}

      <div className="input-container__sufix">
        {sufix}
        {
          (!isClassicInput && !isEmptyValue && !hideSingleClearState ) &&
            <a
              type="button"
              className="single-clear-state"
              onClick={handleClearState}
            >
              <span className="sufix-divider"></span>
              
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.8337 5.34169L14.6587 4.16669L10.0003 8.82502L5.34199 4.16669L4.16699 5.34169L8.82533 10L4.16699 14.6584L5.34199 15.8334L10.0003 11.175L14.6587 15.8334L15.8337 14.6584L11.1753 10L15.8337 5.34169Z"
                    fill="black"
                  />
                </svg>
            </a>
        }
      </div>

      {(label.trim() !== "" && label.trim() !== " ") && (
        <div className="label-container">
          <label
            className="label-container__label"
            htmlFor={`input-${id || attr || label}`}
          >
            {t(label)}
            <span
              className={`label-container__required ${
                hideRequiredAsterisk() ? "hide" : ""
              }`}
            >
              *
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

export default withTranslation()(BaseField);